import { Group, GroupDetailsType } from '@types';

import { PaymentCollectionType } from '../constants';
import { getPaymentCollectionType } from '../helper';

const FREQUENCY_ANALYTICS_MAP: Record<string, string> = {
  [PaymentCollectionType.MONTHLY]: 'monthly',
  [PaymentCollectionType.WEEKLY]: 'weekly',
  [PaymentCollectionType.ONCE]: 'once',
  [PaymentCollectionType.BY_TERM]: 'term',
  [PaymentCollectionType.CUSTOM_TERM]: 'advance term',
  [PaymentCollectionType.SESSION_BASED]: 'session based'
};

export const getGroupFrequency = (
  frequency: string,
  intervalFrequency: number
) => {
  const paymentType = getPaymentCollectionType(frequency, intervalFrequency);
  return FREQUENCY_ANALYTICS_MAP[paymentType] || frequency;
};

export const getFeeCollectionMethod = (group: GroupDetailsType) =>
  group.attendance_config?.is_attendance_based_fee_enabled
    ? 'attendance_based_fee'
    : 'fixed_fee';

const getPaymentCollectionDay = (group: GroupDetailsType) => {
  const schedule = group.payment_schedule;
  if (!schedule) return undefined;
  return (
    schedule.monthly_config?.custom_day ||
    schedule.weekly_config?.dayOfWeek ||
    undefined
  );
};

export const getGroupAnalyticsPayload = (group: GroupDetailsType) => ({
  group_name: group.name,
  member_count: group.member_count,
  fee_collection_method: getFeeCollectionMethod(group),
  group_frequency: getGroupFrequency(group.frequency, group.interval_frequency),
  group_due_date: group.next_due_at,
  group_activation_date: group.activation_date,
  group_deactivation_date: group.end_date,
  group_amount: group.amount,
  is_attendance_enabled:
    group.attendance_config?.is_attendance_tracking_enabled ||
    group.is_attendance_tracking_enabled ||
    false,
  is_split_payment_enabled: group.is_split_payment || false,
  payment_collection_day: getPaymentCollectionDay(group),
  payment_collection_start_date:
    group.start_date || group.collection_started_at,
  group_status: group.is_active ? 'active' : 'inactive',
  session_count: group.payment_schedule?.sessions_per_cycle
});

export const getGroupDetailsPayload = (group: GroupDetailsType) => {
  const {
    group_frequency: groupFrequency,
    group_due_date: groupDueDate,
    session_count: sessionCount,
    ...basePayload
  } = getGroupAnalyticsPayload(group);

  return {
    ...basePayload,
    frequency: groupFrequency,
    due_date: groupDueDate,
    ...(basePayload.is_attendance_enabled && {
      send_absence_alert:
        group.attendance_config?.is_send_attendance_alert_enabled ||
        group.is_send_attendance_alert_enabled ||
        false,
      attendance_days: group.work_days || group.attendance_config?.working_days
    }),
    ...(groupFrequency === 'session based' && { session_count: sessionCount })
  };
};

export const getGroupCardEditClickedPayload = (group: Group) => ({
  group_name: group.name,
  member_count: group.member_count,
  fee_collection_method: group.is_attendance_based_fee_enabled
    ? 'attendance_based_fee'
    : 'fixed_fee',
  frequency: getGroupFrequency(group.interval, group.intervalFrequency),
  group_deactivation_date: group.end_date,
  group_amount: group.amount,
  is_split_payment_enabled: group.is_split_payment || false,
  group_status: group.is_active ? 'active' : 'inactive'
});

export const getSharePromptPayload = (group: GroupDetailsType) => ({
  group_name: group.name,
  member_count: group.member_count,
  fee_collection_method: getFeeCollectionMethod(group),
  frequency: getGroupFrequency(group.frequency, group.interval_frequency),
  due_date: group.next_due_at,
  group_activation_date: group.activation_date,
  group_deactivation_date: group.end_date,
  group_amount: group.amount
});

export const getGroupAnalyticsSummary = (group: GroupDetailsType) => ({
  group_name: group.name,
  group_status: group.is_active ? 'active' : 'inactive',
  group_frequency: getGroupFrequency(group.frequency, group.interval_frequency),
  fee_collection_method: getFeeCollectionMethod(group)
});

export const getGroupTransactionContext = (group: GroupDetailsType) => ({
  group_name: group.name,
  fee_collection_method: getFeeCollectionMethod(group),
  group_status: group.is_active ? 'active' : 'inactive',
  group_frequency: getGroupFrequency(group.frequency, group.interval_frequency)
});

export const getGroupQuickCollectContext = (
  group: GroupDetailsType,
  branchName: string
) => ({
  branch_name: branchName,
  group_name: group.name,
  group_amount: group.amount,
  fee_collection_method: getFeeCollectionMethod(group),
  group_status: group.is_active ? 'active' : 'inactive',
  group_frequency: getGroupFrequency(group.frequency, group.interval_frequency)
});
